import React, { useCallback, useEffect, useRef, useMemo, useState, useContext } from 'react'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { debounce, throttle } from 'lodash'
import { SliderTimeContext } from 'contexts/SliderTimeContext'
import { useAction } from 'hooks'
import * as Actions from '~/actions'
import { REWIND_SOURCES } from '~/constant'
import { playerLog } from '~/Util/playerLog'

export const SliderTimeContextProvider = React.memo(({ children }) => {
  const { id } = useParams()
  const lastIdRef = useRef(null)
  const listeners = useRef(new Set())
  const rafIdRef = useRef(null)
  const localTimeRef = useRef(0)

  const sliderTime = useSelector(state => state.timeline.sliderTime)
  const lastRewindSource = useSelector(state => state.timeline.lastRewindSource)

  const updateSlider = useAction(Actions.timeline.updateSlider)
  const updatePlayingAssets = useAction(Actions.timeline.updatePlayingAssets)
  const rewind = useAction(Actions.timeline.rewind)

  const debouncedRewind = useRef(
    debounce(time => rewind(time, REWIND_SOURCES.CONTEXT), 25, { leading: true, trailing: true })
  ).current

  const debouncedUpdatePlayingAssets = useRef(
    throttle(time => updatePlayingAssets(time), 400, { leading: true, trailing: true })
  ).current

  const debouncedUpdateSlider = useRef(
    debounce(time => updateSlider(time), 200, { leading: true, trailing: true })
  ).current

  const subscribe = useCallback(callback => {
    listeners.current.add(callback)
    return () => listeners.current.delete(callback)
  }, [])

  useEffect(() => {
    const isIdChanged = lastIdRef.current !== id

    const shouldUpdateFromStore = isIdChanged || (sliderTime !== localTimeRef.current
        && lastRewindSource === REWIND_SOURCES.DEFAULT)

    if (shouldUpdateFromStore && sliderTime !== undefined) {
      playerLog('[SliderTime] store overwrite localTime', {
        sliderTime,
        localTime: localTimeRef.current,
        lastRewindSource,
        isIdChanged,
      })
      localTimeRef.current = sliderTime
      lastIdRef.current = id

      listeners.current.forEach(callback => callback(sliderTime))
    }
  }, [ sliderTime, id, lastRewindSource ])

  useEffect(() => () => {
    if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current)
  }, [])

  const setLocalTime = useCallback((time, options) => {
    const { isRewind } = options ?? {}
    const prevLocalTime = localTimeRef.current

    localTimeRef.current = time
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current)
    }

    rafIdRef.current = requestAnimationFrame(() => {
      listeners.current.forEach(callback => callback(time))
    })

    if (isRewind) {
      playerLog('[SliderTime] setLocalTime isRewind', { time, prevLocalTime })
      debouncedRewind.cancel()
      debouncedUpdateSlider.cancel()
      debouncedUpdatePlayingAssets.cancel()
      updatePlayingAssets(time)
      debouncedRewind(time)
      return
    }

    debouncedUpdatePlayingAssets(time)
    debouncedUpdateSlider(time)
  }, [ debouncedUpdatePlayingAssets, updatePlayingAssets, debouncedRewind, debouncedUpdateSlider ])

  const contextValue = useMemo(() => ({
    subscribe,
    localTimeRef,
    setLocalTime,
  }), [ localTimeRef, setLocalTime, subscribe ])

  return (
    <SliderTimeContext.Provider value={contextValue}>
      {children}
    </SliderTimeContext.Provider>
  )
})

/**
 * Hook for getting the current time from SliderTimeContext.
 * @param {number} throttleMs - refresh rate (0 - every frame)
 */
export const useSliderTime = (throttleMs = 0) => {
  const context = useContext(SliderTimeContext)

  if (!context) {
    throw new Error('useSliderTime must be used within SliderTimeContextProvider')
  }

  const { localTimeRef, subscribe } = context

  const [ currentTime, setCurrentTime ] = useState(localTimeRef.current)

  useEffect(() => {
    let lastNotify = 0

    const handleChange = newTime => {
      if (throttleMs > 0) {
        const now = Date.now()
        if (now - lastNotify < throttleMs) return
        lastNotify = now
      }

      setCurrentTime(newTime)
    }

    return subscribe(handleChange)
  }, [ subscribe, throttleMs ])

  return currentTime
}
